import { useState } from "react";
import { Box, Typography, Backdrop, CircularProgress, Paper } from "@mui/material";

// Components
import ExpedicaoHeader from "./components/AuxilioExpedicao/ExpedicaoHeader";
import ExpedicaoStats from "./components/AuxilioExpedicao/ExpedicaoStats";
import ExpedicaoFilters from "./components/AuxilioExpedicao/ExpedicaoFilters";
import ExpedicaoTable from "./components/AuxilioExpedicao/ExpedicaoTable";
import ExpedicaoActions from "./components/AuxilioExpedicao/ExpedicaoActions";
import ExpedicaoPagination from "./components/AuxilioExpedicao/ExpedicaoPagination";
import InvoicesModal from "./components/AuxilioExpedicao/InvoicesModal";
import OrderModals from "./components/OrderModals";

// Hooks
import useExpedicaoData from "./hooks/useExpedicaoData";
import useExpedicaoExport from "./hooks/useExpedicaoExport";
import { useGetMarketplaceSkusQuantities, useGetMarketplaceSkusMetrics, useGetMarketplaceOrders } from "../../client/hooks/marketplace/marketplace.queries";
import useBridgeStatus from "./shared/hooks/useBridgeStatus";
import moment from "moment";

const AuxilioExpedicao = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState(moment().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().add(1, "days").format("YYYY-MM-DD"));
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  
  // Printing states
  const [printingState, setPrintingState] = useState({
    isProcessing: false,
    currentInvoice: null,
    processedCount: 0,
    totalCount: 0
  });

  // Hooks da API
  const { data: skusData, isLoading: loadingSkus, refetch: refetchSkus } = useGetMarketplaceSkusQuantities({
    from: dateFrom,
    to: dateTo,
    limit: itemsPerPage,
    page: currentPage
  }, true);

  // Dados da paginação
  const totalPages = Math.ceil((skusData?.pagination?.total_items || 0) / itemsPerPage);
  const totalItems = skusData?.pagination?.total_items || 0;

  const { data: metricsData, isLoading: loadingMetrics, refetch: refetchMetrics } = useGetMarketplaceSkusMetrics({
    from: dateFrom,
    to: dateTo
  }, true);

  const { handleExportList, handlePrintList } = useExpedicaoExport();

  // Bridge status hook
  const {
    isConnected: bridgeConnected,
    printers: availablePrinters,
    selectedPrinter,
    isLoading: loadingPrinters,
    downloadProgress,
    loadPrinters: detectPrinters,
    printLabel: printToBridge,
    selectPrinter,
    downloadBridgeInstaller
  } = useBridgeStatus();

  // Bridge modal states
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const [bridgeInstallModalOpen, setBridgeInstallModalOpen] = useState(false);

  // Hook para buscar invoices do modal
  const { data: invoicesData, isLoading: loadingInvoices, error: invoicesError, refetch: refetchInvoices } = useGetMarketplaceOrders(
    0,
    null,
    null,
    selectedSku ? {
      sku: selectedSku,
      printed: false,
      from: dateFrom,
      to: dateTo
    } : {},
  );

  // Filtrar dados localmente baseado no termo de busca
  const filteredSkus = skusData?.data?.filter(item => {
    const matchesSearch = item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) || [];

  // Estatísticas baseadas nos dados da API
  const stats = {
    totalPendentes: metricsData?.total_invoices || 0,
    totalItens: metricsData?.total_skus || 0,
    totalQuantidade: metricsData?.total_quantity || 0
  };

  const handleRefresh = () => {
    refetchSkus();
    refetchMetrics();
  };

  const handleExport = () => {
    handleExportList(filteredSkus);
  };

  const handlePrint = () => {
    handlePrintList(filteredSkus);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handlePrintTransportLabels = (sku) => {
    setSelectedSku(sku);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSku(null);
  };

  const handleNavigateToSku = (newSku) => {
    setSelectedSku(newSku);
  };

  // Bridge configuration handlers
  const handleConfigurePrinter = () => {
    setPrinterModalOpen(true);
    if (bridgeConnected && availablePrinters.length === 0) {
      detectPrinters();
    }
  };

  const handleInstallBridge = () => {
    setBridgeInstallModalOpen(true);
  };

  const handleSelectPrinter = (printer) => {
    selectPrinter(printer);
    setPrinterModalOpen(false);
  };

  // Função para imprimir etiqueta de transporte individual
  const handlePrintTransportLabel = async (invoice) => {
    if (!bridgeConnected || !invoice) return;

    try {
      setPrintingState(prev => ({
        ...prev,
        isProcessing: true,
        currentInvoice: invoice._id
      }));

      // Buscar a etiqueta de transporte ZPL usando API diretamente
      const { api } = await import("../../client/config");
      
      const labelResponse = await api.get(`/marketplace/invoices/${invoice.chave_acesso}/label`, {
        params: { response_type: 'zpl' }
      });
      
      if (labelResponse.data) {
        // Imprimir etiqueta de transporte no bridge
        await printToBridge(labelResponse.data);
        
        // Marcar etiqueta como impressa
        await api.patch(`/marketplace/invoices/${invoice.chave_acesso}/printed`, {
          printed: true
        });
        
        // Atualizar o status da etiqueta localmente
        invoice.printed = true;
        
        // Refresh da lista de etiquetas
        setTimeout(() => {
          refetchInvoices();
        }, 500);
      }

    } catch (error) {
      console.error('Erro ao imprimir etiqueta de transporte:', error);
    } finally {
      setPrintingState(prev => ({
        ...prev,
        isProcessing: false,
        currentInvoice: null
      }));
    }
  };

  // Função para imprimir etiquetas de transporte em lote (apenas as elegíveis sem múltiplos itens)
  const handlePrintAllTransportLabels = async () => {
    if (!bridgeConnected || !invoicesData?.orders?.length) return;

    // Filtrar apenas as etiquetas que têm o SKU específico e não têm outros itens
    const transportLabelsToPrint = invoicesData.orders.filter(invoice => {
      if (!invoice.content?.items_list || !Array.isArray(invoice.content.items_list)) {
        return true; // Se não tem items_list, assume que é elegível
      }
      
      const currentItem = invoice.content.items_list.find(item => item.item_sku === selectedSku);
      const otherItems = invoice.content.items_list.filter(item => item.item_sku !== selectedSku);
      
      // Inclui apenas etiquetas que têm o SKU específico e não têm outros itens
      return currentItem && otherItems.length === 0;
    });

    if (transportLabelsToPrint.length === 0) {
      console.log('Nenhuma etiqueta de transporte elegível para impressão em lote');
      return;
    }
    
    setPrintingState({
      isProcessing: true,
      currentInvoice: null,
      processedCount: 0,
      totalCount: transportLabelsToPrint.length
    });

    try {
      const { api } = await import("../../client/config");
      
      for (let i = 0; i < transportLabelsToPrint.length; i++) {
        const invoice = transportLabelsToPrint[i];
        
        setPrintingState(prev => ({
          ...prev,
          currentInvoice: invoice._id,
          processedCount: i
        }));

        try {
          // Buscar a etiqueta de transporte ZPL
          const labelResponse = await api.get(`/marketplace/invoices/${invoice.chave_acesso}/label`, {
            params: { response_type: 'zpl' }
          });
          
          if (labelResponse.data) {
            // Imprimir etiqueta de transporte no bridge
            await printToBridge(labelResponse.data);
            
            // Marcar etiqueta como impressa
            await api.patch(`/marketplace/invoices/${invoice.chave_acesso}/printed`, {
              printed: true
            });
            
            // Atualizar o status da etiqueta localmente
            invoice.printed = true;
          }

          // Intervalo de 500ms entre impressões de etiquetas
          if (i < transportLabelsToPrint.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

        } catch (error) {
          console.error(`Erro ao imprimir etiqueta de transporte ${invoice.chave_acesso}:`, error);
          // Continue com a próxima etiqueta mesmo se houver erro
        }
      }

    } finally {
      setPrintingState({
        isProcessing: false,
        currentInvoice: null,
        processedCount: 0,
        totalCount: 0
      });
      
      // Refresh da lista de etiquetas após impressão em lote
      setTimeout(() => {
        refetchInvoices();
      }, 1000);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f4f4f4",
        width: "100%",
        margin: "auto",
        borderRadius: "12px",
        boxShadow: 8,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: "90vh",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          zIndex: 1000,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: 0,
            height: 0,
            borderLeft: "60px solid transparent",
            borderTop: "60px solid #FF7322",
          }
        }}
      >
        <Typography
          sx={{
            position: "absolute",
            top: "12px",
            right: "6px",
            transform: "rotate(45deg)",
            transformOrigin: "center",
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "white",
            textTransform: "uppercase",
            letterSpacing: "1px",
            userSelect: "none",
            pointerEvents: "none"
          }}
        >
            BETA
        </Typography>
      </Box>
      
      <ExpedicaoHeader 
        bridgeConnected={bridgeConnected}
        selectedPrinter={selectedPrinter}
        onConfigurePrinter={handleConfigurePrinter}
        onInstallBridge={handleInstallBridge}
      />
      
      <ExpedicaoStats 
        totalPendentes={stats.totalPendentes}
        totalItens={stats.totalItens}
        totalQuantidade={stats.totalQuantidade}
      />
      
      <ExpedicaoFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        loading={loadingSkus || loadingMetrics}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onPrint={handlePrint}
      />
      
      <ExpedicaoPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      
      <ExpedicaoTable 
        data={filteredSkus}
        loading={loadingSkus || loadingMetrics}
        showActions={true}
        onPrintInvoices={handlePrintTransportLabels}
      />
      
      <ExpedicaoActions
        totalItems={filteredSkus.length}
      />

      {/* Modal de Invoices */}
      <InvoicesModal
        open={modalOpen}
        onClose={handleCloseModal}
        invoices={invoicesData?.orders || []}
        loading={loadingInvoices}
        sku={selectedSku}
        error={invoicesError?.message}
        onNavigateToSku={handleNavigateToSku}
        onPrintInvoice={handlePrintTransportLabel}
        onPrintAll={handlePrintAllTransportLabels}
        bridgeConnected={bridgeConnected}
        printingState={printingState}
      />

      {/* Modais do Bridge */}
      <OrderModals
        printerModalOpen={printerModalOpen}
        handleClosePrinterModal={() => setPrinterModalOpen(false)}
        availablePrinters={availablePrinters}
        selectedPrinter={selectedPrinter}
        loadingPrinters={loadingPrinters}
        handleSelectPrinter={handleSelectPrinter}
        detectPrinters={detectPrinters}
        autoPrintEnabled={false}
        bridgeInstallModalOpen={bridgeInstallModalOpen}
        setBridgeInstallModalOpen={setBridgeInstallModalOpen}
        downloadProgress={downloadProgress}
        downloadBridgeInstaller={downloadBridgeInstaller}
        showForceDialog={false}
        handleCloseForceDialog={() => {}}
        handleConfirmForceDialog={() => {}}
        reprintDialogOpen={false}
        setReprintDialogOpen={() => {}}
        pendingReprintOrder={null}
        onReprintConfirm={() => {}}
        snackbarOpen={false}
        handleCloseSnackbar={() => {}}
        snackbarSeverity="info"
        snackbarMessage=""
      />

      {/* Overlay de Impressão em Lote */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }}
        open={printingState?.isProcessing && printingState?.totalCount > 1}
      >
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
            <CircularProgress
              variant="determinate"
              value={printingState?.totalCount ? ((printingState.processedCount) / printingState.totalCount) * 100 : 0}
              size={80}
              thickness={4}
              sx={{
                color: '#FF7322',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{ 
                  color: '#FF7322', 
                  fontWeight: 700,
                  fontSize: '0.9rem'
                }}
              >
                {printingState?.totalCount ? Math.round(((printingState.processedCount) / printingState.totalCount) * 100) : 0}%
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: '#333',
              fontWeight: 600
            }}
          >
            🏷️ Impressão de Etiquetas em Lote
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: '#666'
            }}
          >
            Processando {printingState?.processedCount + 1} de {printingState?.totalCount} etiquetas de transporte
          </Typography>

          <Box
            sx={{
              bgcolor: 'rgba(255, 115, 34, 0.1)',
              borderRadius: 2,
              p: 2,
              border: '1px solid rgba(255, 115, 34, 0.2)'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#FF7322',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            >
              ⚠️ Aguarde a conclusão do processo
            </Typography>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default AuxilioExpedicao;